import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Trash2, Save, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { API, auth, guarded, inputCls, btnPrimary, btnGhost } from "./shared";

export default function Outreach({ token, onAuthError }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  const load = useCallback(
    () =>
      guarded(async () => {
        const { data } = await axios.get(`${API}/outreach`, auth(token));
        setItems(data);
      }, onAuthError),
    [token, onAuthError]
  );

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const sendAll = async () => {
    const ready = (items || []).filter((d) => d.status === "draft" && d.school_email);
    if (!ready.length) {
      toast.error("No drafts with an email address are ready to send.");
      return;
    }
    if (!window.confirm(`Approve and send ${ready.length} outreach emails now?`)) return;
    setBusyAll(true);
    await guarded(async () => {
      const { data } = await axios.post(`${API}/outreach/send-all`, {}, auth(token));
      toast.success(`Sent ${data.sent} · skipped ${data.skipped} (no email) · failed ${data.failed}.`);
      await load();
    }, onAuthError);
    setBusyAll(false);
  };

  const formValues = (id) => {
    const formEl = document.querySelector(`[data-testid="outreach-card-${id}"]`);
    const fd = new FormData(formEl);
    return { subject: fd.get("subject"), body: fd.get("body") };
  };

  const save = async (e, id) => {
    e.preventDefault();
    setBusyId(id);
    await guarded(async () => {
      await axios.patch(`${API}/outreach/${id}`, formValues(id), auth(token));
      toast.success("Draft saved.");
      await load();
    }, onAuthError);
    setBusyId(null);
  };

  const sendDraft = async (id) => {
    setBusyId(id);
    await guarded(async () => {
      try {
        await axios.patch(`${API}/outreach/${id}`, formValues(id), auth(token));
        await axios.post(`${API}/outreach/${id}/send`, {}, auth(token));
        toast.success("Outreach email sent and logged to the school's history.");
      } catch (e2) {
        toast.error(e2.response?.data?.detail || "Could not send the email.");
      }
      await load();
    }, onAuthError);
    setBusyId(null);
  };

  const remove = (id) => {
    if (!window.confirm("Delete this draft?")) return;
    guarded(async () => {
      await axios.delete(`${API}/outreach/${id}`, auth(token));
      await load();
    }, onAuthError);
  };

  if (!items) {
    return (
      <div className="flex items-center gap-3 py-20 text-[#8A837B]">
        <Loader2 size={18} className="animate-spin" /> Loading outreach…
      </div>
    );
  }

  const drafts = items.filter((d) => d.status === "draft");
  const sent = items.filter((d) => d.status === "sent");

  return (
    <div data-testid="outreach-tab">
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <p className="max-w-xl text-sm text-[#6B655F]">
          AI-drafted, personalized emails to schools. Review, edit if needed, then approve — the
          email sends automatically and is logged to the school's history.
        </p>
        <span className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[#8A837B]" data-testid="outreach-count">
            {drafts.length} draft{drafts.length === 1 ? "" : "s"} · {sent.length} sent
          </span>
          <button
            data-testid="outreach-send-all"
            onClick={sendAll}
            disabled={busyAll || drafts.filter((d) => d.school_email).length === 0}
            className={btnPrimary}
          >
            {busyAll ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Approve & Send All ({drafts.filter((d) => d.school_email).length})
          </button>
        </span>
      </div>

      {items.length === 0 && (
        <div className="flex flex-col items-center gap-3 border border-[#E6E2DC] bg-white py-16 text-[#8A837B]" data-testid="outreach-empty">
          <Mail size={36} strokeWidth={1.4} />
          <p className="text-sm">No outreach yet — draft emails from Schools → Top Prospects, or ask the AI Agent.</p>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {items.map((d) => {
          const isSent = d.status === "sent";
          return (
            <form
              key={d.id}
              data-testid={`outreach-card-${d.id}`}
              onSubmit={(e) => save(e, d.id)}
              className="border border-[#E6E2DC] bg-white p-5"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-[#141210]">{d.school_name}</span>
                <span className="border border-[#E6E2DC] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#6B655F]">
                  {(d.lang || "en").toUpperCase()}
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${isSent ? "bg-[#1E7A5A]/10 text-[#1E7A5A]" : "bg-[#B45309]/10 text-[#B45309]"}`}>
                  {d.status}
                </span>
                {d.school_email ? (
                  <span className="text-xs text-[#6B655F]">to {d.school_email}</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-semibold text-[#B45309]">
                    <AlertTriangle size={12} /> No email on file — add one on the school card to send
                  </span>
                )}
                {isSent && d.sent_at && (
                  <span className="flex items-center gap-1 text-xs text-[#1E7A5A]">
                    <CheckCircle2 size={12} /> Sent {new Date(d.sent_at).toLocaleString()}
                  </span>
                )}
              </div>
              <input
                name="subject"
                defaultValue={d.subject}
                disabled={isSent}
                data-testid={`outreach-subject-${d.id}`}
                className={`${inputCls} mt-4 font-semibold`}
                placeholder="Subject"
              />
              <textarea
                name="body"
                defaultValue={d.body}
                disabled={isSent}
                rows={9}
                data-testid={`outreach-body-${d.id}`}
                className="mt-3 w-full resize-y border border-[#E6E2DC] bg-[#FAF8F5] p-4 text-sm leading-relaxed text-[#141210] focus:border-[#581820] focus:outline-none"
              />
              <div className="mt-4 flex flex-wrap gap-3">
                {!isSent && (
                  <>
                    <button type="submit" data-testid={`outreach-save-${d.id}`} disabled={busyId === d.id} className={btnGhost}>
                      <Save size={13} /> Save
                    </button>
                    <button
                      type="button"
                      data-testid={`outreach-send-${d.id}`}
                      disabled={busyId === d.id || !d.school_email}
                      onClick={() => sendDraft(d.id)}
                      className={btnPrimary}
                      title={d.school_email ? "" : "Add the school's email first"}
                    >
                      {busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                      Approve & Send
                    </button>
                  </>
                )}
                <button
                  type="button"
                  data-testid={`outreach-delete-${d.id}`}
                  onClick={() => remove(d.id)}
                  className="flex items-center gap-2 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#8A837B] transition-colors duration-300 hover:text-[#581820]"
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </form>
          );
        })}
      </div>
    </div>
  );
}
